import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Button, Form, FormInstance, FormListFieldData, Select } from "antd";
import { sortBy } from "lodash";
import React, { useEffect, useState } from "react";
import { PracticeAreasCascader, VpdList } from "~/design-system";
import { DraggableHandle } from "~/design-system/components/draggable-list/draggable-handle";
import { SortableItem } from "~/design-system/components/draggable-list/sortable-item";
import { mergeArraysOfObjectsByProp } from "~/helpers";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { DeleteOutlined } from "@ant-design/icons";
import { OfficeLocationSelect } from "~/design-system/components/office-location/office-location-select";
import { DynamicTagConditionAndOr } from "~/gql/graphql";
import { ContentFormItem } from "./form/ContentFormItem";

type DynamicTagsEntriesProps = {
  items: FormListFieldData[];
  onMove: (from: number, to: number) => void;
  selectedTagIndex: number;
  removeEntry: (name: number) => void;
  form: FormInstance;
};

export const DynamicTagsEntries = ({
  items,
  onMove,
  selectedTagIndex,
  removeEntry,
  form,
}: DynamicTagsEntriesProps) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const [sortedItems, setItemsOrder] = useState<FormListFieldData[]>(items);

  useEffect(() => {
    setItemsOrder(items);
  }, [items]);

  const onReOrder = (activeId: number, overId: number) => {
    const sortedItemsCopy = sortedItems.map((item) => ({ ...item }));
    const activeIndex = sortedItemsCopy.findIndex((d) => d.key === activeId);
    const activeItem = sortedItemsCopy[activeIndex];
    const overIndex = sortedItemsCopy.findIndex((d) => d.key === overId);

    const updates = [];
    // Drag Down

    if (activeIndex < overIndex) {
      sortedItemsCopy.forEach((item, index) => {
        if (index <= overIndex && index !== activeIndex) {
          updates.push({ ...item, order: index - 1 });
        }
      });
      updates.push({ ...activeItem, order: overIndex });
    }
    // Drag Up
    else if (activeIndex > overIndex) {
      sortedItemsCopy.forEach((item, index) => {
        if (index >= overIndex && index !== activeIndex) {
          updates.push({ ...item, order: index + 1 });
        }
      });
      updates.push({ ...activeItem, order: overIndex });
    }

    const merged = sortBy(mergeArraysOfObjectsByProp(updates, sortedItemsCopy, "name"), "order");
    setItemsOrder(merged);

    onMove(activeIndex, overIndex);
  };

  return (
    <DndContext
      onDragEnd={({ active, over }) => over && onReOrder(parseInt(active.id), parseInt(over.id))}
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={sortedItems.map((x) => x.key.toString())}
        strategy={verticalListSortingStrategy}
      >
        <VpdList
          items={sortBy(sortedItems, "order")}
          split
          itemKeyProperty="key"
          itemTextProperty="name"
          labels={{ emptyText: "" }}
          showEmptyText={false}
          listStyle="primary"
          renderItem={(entry, entryIndex) => (
            <SortableItem id={entry.key.toString()} key={entry.key}>
              {({ attributes, listeners }) => (
                <VpdList.Item
                  contentPrefix={<DraggableHandle {...attributes} {...listeners} />}
                  key={entry.key}
                  content={
                    <div
                      key={entry.key}
                      style={{ display: "flex", gap: "8px", paddingTop: "20px" }}
                    >
                      <Form.Item
                        rules={[{ required: true, message: "Type is required" }]}
                        name={[entry.name, "contentType"]}
                        style={{ width: "150px" }}
                      >
                        <Select placeholder="Select type">
                          <Select.Option value="Text">Text</Select.Option>
                          <Select.Option value="Html">HTML</Select.Option>
                          <Select.Option value="Image">Image</Select.Option>
                        </Select>
                      </Form.Item>
                      <ContentFormItem
                        form={form}
                        selectedTagIndex={selectedTagIndex}
                        entry={entry}
                      />
                      <Form.Item
                        name={[entry.name, "condition", "organisationalUnitId"]}
                        style={{ margin: 0, width: "250px" }}
                        getValueFromEvent={(e, ...args) => {
                          const opt = findOptionPath(args[0], e);
                          return opt;
                        }}
                        getValueProps={(e) => {
                          return {
                            value: e,
                          };
                        }}
                      >
                        <PracticeAreasCascader />
                      </Form.Item>
                      <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => (
                          <Form.Item
                            required
                            style={{ margin: 0, width: "100px" }}
                            name={[entry.name, "condition", "condition"]}
                          >
                            <Select
                              disabled={
                                !getFieldValue([
                                  "tags",
                                  selectedTagIndex,
                                  "entries",
                                  entryIndex,
                                  "condition",
                                  "organisationalUnitId",
                                ])
                              }
                              placeholder="and/or"
                              allowClear
                              options={[
                                {
                                  label: "and",
                                  value: DynamicTagConditionAndOr.And,
                                },
                                {
                                  label: "or",
                                  value: DynamicTagConditionAndOr.Or,
                                },
                              ]}
                            />
                          </Form.Item>
                        )}
                      </Form.Item>
                      <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue, setFieldValue }) => (
                          <Form.Item
                            style={{ margin: 0, width: "200px" }}
                            name={[entry.name, "condition", "officeId"]}
                          >
                            <OfficeLocationSelect
                              onChange={(e) => {
                                const orgUnitId = getFieldValue([
                                  "tags",
                                  selectedTagIndex,
                                  "entries",
                                  entryIndex,
                                  "condition",
                                  "organisationalUnitId",
                                ]);
                                const AndOrValue = getFieldValue([
                                  "tags",
                                  selectedTagIndex,
                                  "entries",
                                  entryIndex,
                                  "condition",
                                  "condition",
                                ]);
                                const newValue = e;
                                const orgUnit = Array.isArray(orgUnitId)
                                  ? orgUnitId.length > 0
                                  : orgUnitId;
                                if (newValue && !!orgUnit && !AndOrValue) {
                                  setFieldValue(
                                    [
                                      "tags",
                                      selectedTagIndex,
                                      "entries",
                                      entryIndex,
                                      "condition",
                                      "condition",
                                    ],
                                    DynamicTagConditionAndOr.And
                                  );
                                }
                              }}
                              // disabled={
                              //   !getFieldValue([
                              //     "tags",
                              //     selectedTagIndex,
                              //     "entries",
                              //     entryIndex,
                              //     "condition",
                              //     "condition",
                              //   ])
                              // }
                            />
                          </Form.Item>
                        )}
                      </Form.Item>
                      <Button
                        icon={<DeleteOutlined />}
                        type="text"
                        danger
                        onClick={() => removeEntry(entry.name)}
                      />
                    </div>
                  }
                ></VpdList.Item>
              )}
            </SortableItem>
          )}
        />
      </SortableContext>
    </DndContext>
  );
};
const findOptionPath = (options: any[], targetValue: number): number[] => {
  for (const option of options) {
    if (option.value === targetValue) {
      return [targetValue];
    }
    if (option.children) {
      const childPath = findOptionPath(option.children, targetValue);
      if (childPath.length > 0) {
        const path = [option.value as number, ...childPath];
        return path;
      }
    }
  }
  return [];
};
